import React, { useState, useEffect } from 'react';
import { Factory, Hourglass, Calendar, CheckSquare, User, Package, Clock, RotateCcw } from 'lucide-react';
import { ToastContainer } from 'react-toastify';
// Nota: react-toastify no está implementado para mantener el foco en la funcionalidad principal.

// Función auxiliar para obtener la fecha de hoy en formato YYYY-MM-DD
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Interfaz para tipar las propiedades de ResultCard y solucionar el error 'implicitly has an any type'
interface ResultCardProps {
    title: string;
    value: number;
    icon: React.ElementType;
    color: string;
}

const FeedDaysCalculator = () => {
  // Estado para los inputs
  const [customerName, setCustomerName] = useState('');
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [totalDaysPurchased, setTotalDaysPurchased] = useState(365); // Valor inicial común: 1 año

  // Estado para los resultados del cálculo
  const [daysPassed, setDaysPassed] = useState(0);
  const [daysRemaining, setDaysRemaining] = useState(365);

  // Lógica principal de cálculo
  const calculateDays = () => {
    if (!startDate || totalDaysPurchased <= 0) {
      setDaysPassed(0);
      setDaysRemaining(0);
      return;
    }

    // Convertir la fecha de inicio y la fecha actual a objetos Date
    const start = new Date(startDate);
    const today = new Date();

    // Normalizar las fechas a medianoche para un cálculo de días preciso (diferencia solo de días, no horas)
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Calcular la diferencia en milisegundos
    const diffTime = today.getTime() - start.getTime();

    let calculatedDaysPassed = 0;

    if (diffTime >= 0) {
      // Convertir milisegundos a días.
      // Math.floor para obtener días completos. Se suma 1 para incluir el día de alta.
      calculatedDaysPassed = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } else {
      // Si la fecha de inicio es futura, los días pasados son 0
      calculatedDaysPassed = 0;
    }

    // Calcular días restantes, asegurando que no sea negativo
    const calculatedDaysRemaining = Math.max(0, totalDaysPurchased - calculatedDaysPassed);

    setDaysPassed(calculatedDaysPassed);
    setDaysRemaining(calculatedDaysRemaining);
  };

  // Ejecutar el cálculo cada vez que cambian los inputs
  useEffect(() => {
    calculateDays();
  }, [startDate, totalDaysPurchased]);

  // Estilos de Tailwind CSS para una interfaz limpia y responsiva
  const inputStyle = "w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out";
  const labelStyle = "block text-sm font-medium text-gray-700 mb-1";
  const iconWrapperStyle = "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400";

  // Componente ResultCard ahora tipado correctamente
  const ResultCard: React.FC<ResultCardProps> = ({ title, value, icon: Icon, color }) => (
    <div className={`p-4 rounded-xl shadow-md ${color} flex flex-col items-start transition transform hover:scale-[1.02] duration-300`}>
      <div className="flex items-center space-x-2 mb-1">
        <Icon className="w-5 h-5 text-current" />
        <h3 className="text-sm font-semibold text-current">{title}</h3>
      </div>
      <p className="text-3xl font-extrabold text-current">{value}</p>
      <p className="text-xs opacity-80 text-current">días</p>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center p-4 sm:p-6 md:p-8">
      <div className="relative bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-4xl mt-10">
        <h2 className="text-2xl font-extrabold text-gray-800 mb-6 border-b pb-2 flex items-center">
          <Clock className="w-6 h-6 mr-2 text-blue-600" />
          Calculadora de Días de Licencia
        </h2>
        
        <p className="text-sm text-gray-500 mb-6">
          Ingrese la información del cliente y la licencia para calcular los días transcurridos hasta hoy y los días restantes.
        </p>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-6">
          {/* Sección de Datos del Cliente 
            <div className="space-y-4">
                <label className={labelStyle} htmlFor="customerName">
                  <User className="inline w-4 h-4 mr-1" /> Nombre de la Empresa / Propietario
                </label>
                <div className="relative">
                  <div className={iconWrapperStyle}>
                    <Factory size={18} />
                  </div>
                  <input
                    className={inputStyle}
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Empresa o Propietario"
                    required
                  />
                </div>
            </div>
            */}
            
            <div className="space-y-4">
              <label className={labelStyle} htmlFor="totalDaysPurchased">
                <Package className="inline w-4 h-4 mr-1" /> Días Totales Comprados
              </label>
              <div className="relative">
                <div className={iconWrapperStyle}>
                  <Hourglass size={18} />
                </div>
                <input
                  className={inputStyle}
                  type="number"
                  id="totalDaysPurchased"
                  name="totalDaysPurchased"
                  value={totalDaysPurchased}
                  onChange={(e) => setTotalDaysPurchased(parseInt(e.target.value) || 0)}
                  placeholder="Ej: 365"
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Sección de Fechas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-6">
            <div className="space-y-4">
              <label className={labelStyle} htmlFor="startDate">
                <Calendar className="inline w-4 h-4 mr-1" /> Fecha de Alta (Inicio de Licencia)
              </label>
              <div className="relative">
                <div className={iconWrapperStyle}>
                  <Calendar size={18} />
                </div>
                <input
                  className={inputStyle}
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className={labelStyle}>
                <CheckSquare className="inline w-4 h-4 mr-1" /> Fecha de Cálculo (Hoy)
              </label>
              <div className="relative">
                <div className={iconWrapperStyle}>
                  <Clock size={18} />
                </div>
                <input
                  className={`${inputStyle} bg-gray-50 text-gray-600 cursor-not-allowed`}
                  type="text"
                  value={getTodayDateString()}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Sección de Resultados */}
          <div className="pt-4">
            <h3 className="text-xl font-bold text-gray-700 mb-4">
              <Package className="inline w-5 h-5 mr-2 text-blue-600" /> Resumen de la Licencia
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ResultCard
                title="Días Totales Comprados"
                value={totalDaysPurchased}
                icon={Package}
                color="bg-blue-100 text-blue-800"
              />
              <ResultCard
                title="Días Transcurridos"
                value={daysPassed}
                icon={Hourglass}
                color="bg-yellow-100 text-yellow-800"
              />
              <ResultCard
                title="Días Restantes"
                value={daysRemaining}
                icon={RotateCcw}
                color="bg-green-100 text-green-800"
              />
            </div>
            
            {daysPassed > 0 && (
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
                <p>
                  Desde la fecha de alta del cliente ({startDate}), han transcurrido <strong className="text-yellow-800">{daysPassed} días</strong> hasta el día de hoy ({getTodayDateString()}).
                </p>
                <p>
                  Al restar los días transcurridos de los días totales comprados ({totalDaysPurchased}), quedan <strong className="text-green-800">{daysRemaining} días</strong> de licencia restantes.
                </p>
              </div>
            )}
            
            {startDate > getTodayDateString() && (
               <div className="mt-6 p-4 bg-red-100 border border-red-300 rounded-xl text-sm text-red-800">
                <p>
                  La fecha de alta ({startDate}) es futura. El contador de días transcurridos comenzará al llegar a esa fecha.
                </p>
              </div>
            )}

          </div>

        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default FeedDaysCalculator;
